import React, { FC, useContext } from 'react';
import { UeElement, UeReference } from '@oneaudi/falcon-tools';
import { Text } from '@oneaudi/unified-web-components';
import { renderTextWithFootnotesReferencesV2 } from '@oneaudi/feature-app-utils';
import styled from 'styled-components';
import {
  CMP_COLOR_UI_VALUE_FOREGROUND_TEXT_CAPTION_DARK,
  CMP_COLOR_UI_VALUE_FOREGROUND_TEXT_LABEL_DARK,
  CMP_TYPOGRAPHY_UI_VALUE_LG_CAPTION,
  CMP_TYPOGRAPHY_UI_VALUE_LG_LABEL,
  CMP_TYPOGRAPHY_UI_VALUE_MD_CAPTION,
  CMP_TYPOGRAPHY_UI_VALUE_MD_LABEL,
  CMP_TYPOGRAPHY_UI_VALUE_SM_CAPTION,
  CMP_TYPOGRAPHY_UI_VALUE_SM_LABEL,
  SYS_SPACE_FIXED_100,
  SYS_SPACE_FIXED_150,
  SYS_SPACE_FIXED_200,
  SYS_SPACE_FIXED_50,
} from '@oneaudi/unified-web-common';
import {
  CMP_TYPOGRAPHY_UI_VALUE_LG_HEADLINE,
  CMP_TYPOGRAPHY_UI_VALUE_MD_HEADLINE,
  CMP_TYPOGRAPHY_UI_VALUE_SM_HEADLINE,
} from '@oneaudi/unified-web-alpha-common';
import { Alignment, ModuleType, NumbersItem } from '../../types';
import { ComponentContainer, Row } from './NumbersMode.style';
import { Context } from '../Context';
import { EditorialCTA } from './EditorialCTA';
import { convertRichText } from '../utils/richtextUtils';
import { getTypographyBySize } from '../utils/theme';
import { useNumbersSize } from '../hooks/useNumbersSize';
import { TextComponentsContainer } from './TextMode.style';

interface NumbersProps {
  components: NumbersItem[][];
  alignmentNumbers: Alignment;
  flexibleWidth?: boolean;
  ctaComponentList?: NumbersItem[];
}

export enum NumbersSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

const getCaptionStyle = (size: NumbersSize) =>
  getTypographyBySize(
    size,
    CMP_TYPOGRAPHY_UI_VALUE_SM_CAPTION,
    CMP_TYPOGRAPHY_UI_VALUE_MD_CAPTION,
    CMP_TYPOGRAPHY_UI_VALUE_LG_CAPTION,
  );

const getLabelTypographyByEnv = (): [object, object, object] => {
  if (process.env.IS_LEGACY) {
    return [
      CMP_TYPOGRAPHY_UI_VALUE_SM_LABEL,
      CMP_TYPOGRAPHY_UI_VALUE_MD_LABEL,
      CMP_TYPOGRAPHY_UI_VALUE_LG_LABEL,
    ];
  }

  return [
    CMP_TYPOGRAPHY_UI_VALUE_SM_HEADLINE,
    CMP_TYPOGRAPHY_UI_VALUE_MD_HEADLINE,
    CMP_TYPOGRAPHY_UI_VALUE_LG_HEADLINE,
  ];
};

const getLabelStyle = (size: NumbersSize) =>
  getTypographyBySize(size, ...getLabelTypographyByEnv());

const StyledValueUnitContainer = styled.div<{ alignment: Alignment }>`
  display: flex;
  gap: ${SYS_SPACE_FIXED_200};
  align-items: flex-end;
  justify-content: ${(props) => props.alignment};
  padding-bottom: ${SYS_SPACE_FIXED_50};
`;
const StyledTitle = styled(Text)<{ size: NumbersSize }>`
  padding-bottom: ${process.env.IS_LEGACY ? SYS_SPACE_FIXED_50 : 'var(--spacing-relative-md)'};
  ${(props) => getCaptionStyle(props.size)}
  color: ${(props) =>
    process.env.IS_LEGACY
      ? props.theme.cmp.color.ui.value.foreground.caption
      : CMP_COLOR_UI_VALUE_FOREGROUND_TEXT_CAPTION_DARK};
`;

const StyledValue = styled.span<{ size: NumbersSize }>`
  display: flex;
  align-items: start;
  color: ${(props) =>
    process.env.IS_LEGACY
      ? props.theme.cmp.color.ui.value.foreground.label
      : CMP_COLOR_UI_VALUE_FOREGROUND_TEXT_LABEL_DARK};
  ${(props) => getLabelStyle(props.size)}
`;

const StyledUnit = styled(Text)<{ size: NumbersSize }>`
  color: ${(props) =>
    process.env.IS_LEGACY
      ? props.theme.cmp.color.ui.value.foreground.caption
      : CMP_COLOR_UI_VALUE_FOREGROUND_TEXT_CAPTION_DARK};

  text-align: left;
  ${(props) => getCaptionStyle(props.size)}
  padding-bottom: ${(props) =>
    props.size === NumbersSize.LARGE ? SYS_SPACE_FIXED_150 : SYS_SPACE_FIXED_100};

  p {
    margin: 0 !important;
  }
`;

const StyledDescription = styled(Text)<{ size: NumbersSize }>`
  color: ${(props) => props.theme.cmp.color.ui.value.foreground.caption};
  ${(props) => getCaptionStyle(props.size)}
`;
export const Numbers: FC<NumbersProps> = ({
  components,
  alignmentNumbers,
  flexibleWidth,
  ctaComponentList,
}) => {
  const { layerManager } = useContext(Context);
  const size = useNumbersSize();
  return (
    <TextComponentsContainer data-testid="numbers-components-container">
      {components.map((row, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Row flexibleWidth={flexibleWidth} data-testid="numbers-components-row" key={`row-${i}`}>
          {row.map((x: NumbersItem, elementIndex) => (
            <ComponentContainer
              flexibleWidth={flexibleWidth}
              key={`ge-${x.number}`}
              data-testid="numbers-component"
              delay={`${elementIndex * 0.3}s`}
              alignmentNumbers={alignmentNumbers || 'center'}
            >
              <UeReference propertyPath={`moduleContent.components.${i * 3 + elementIndex}`}>
                {x.title ? (
                  <StyledTitle
                    data-testid="numbers-component-title"
                    tag="div"
                    variant={{ type: 'copy', order: '1', style: 'normal' }}
                    key={`ge-title-${x.number}`}
                    size={size}
                  >
                    <UeElement type="text" property="title" label="Title">
                      {renderTextWithFootnotesReferencesV2(x.title)}
                    </UeElement>
                  </StyledTitle>
                ) : (
                  <StyledTitle
                    data-testid=""
                    tag="div"
                    variant={{ type: 'copy', order: '1', style: 'normal' }}
                    size={size}
                  >
                    <span>&nbsp;</span>
                  </StyledTitle>
                )}

                <StyledValueUnitContainer alignment={alignmentNumbers}>
                  {x.number && (
                    <StyledValue
                      data-testid="numbers-component-number"
                      size={size}
                      key={`ge-number-${x.number}`}
                    >
                      <UeElement type="text" property="number" label="Number">
                        {renderTextWithFootnotesReferencesV2(x.number)}
                      </UeElement>
                    </StyledValue>
                  )}
                  {x.unit && (
                    <StyledUnit
                      data-testid="numbers-component-unit"
                      size={size}
                      key={`ge-unit-${x.unit}`}
                    >
                      <UeElement type="text" property="unit" label="Unit">
                        {convertRichText(`<span>${x.unit}</span>`)}
                      </UeElement>
                    </StyledUnit>
                  )}
                </StyledValueUnitContainer>
                {x.description && (
                  <StyledDescription size={size} key={`ge-description-${x.number}`}>
                    <UeElement type="text" property="description" label="Description">
                      {convertRichText(`<span>${x.description}</span>`)}
                    </UeElement>
                  </StyledDescription>
                )}
                {x.cta && (
                  <EditorialCTA
                    mode={ModuleType.NUMBERS}
                    cta={x.cta}
                    ctaComponentList={ctaComponentList}
                    layerManager={layerManager}
                    alignment={alignmentNumbers}
                  />
                )}
              </UeReference>
            </ComponentContainer>
          ))}
        </Row>
      ))}
    </TextComponentsContainer>
  );
};
